import CloseIcon from "@mui/icons-material/Close";
import { IconButton, Typography } from "@mui/material";
import { Button } from "pepsico-ds";
import PropTypes from "prop-types";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { searchHandler } from "../../../../../utils/functions";
import SelectableTable from "../../../../common/selectableTable/SelectableTable";
import ConditionFilters from "../conditionListing/ConditionFilters";
import "./storeOutcome.scss";
import { STORE_SELECTION_TYPES } from "./storeOutcomeUtils";

const FILTERS = [
  { path: "state", label: "State" },
  { path: "city", label: "City" },
  { path: "postcode", label: "Zip Code" },
  { path: "retailer", label: "Retailer" },
  { path: "category", label: "Store groups" },
];

const HEADERS = [
  { columnKey: "name", header: "Name" },
  { columnKey: "address", header: "Address" },
  { columnKey: "state", header: "State" },
  { columnKey: "city", header: "City" },
  { columnKey: "postcode", header: "Zip Code" },
  { columnKey: "latitude", header: "Latitude" },
  { columnKey: "longitude", header: "Longitude" },
  { columnKey: "category", header: "Store Group" },
  { columnKey: "country", header: "Country" },
];

const getStoreValue = (store, path) => {
  const metadata = store.metadata || {};
  if (path === "category") return store.category;
  if (path === "name") return metadata.name || store.name;
  if (path === "latitude") return metadata.geolocation?.latitude;
  if (path === "longitude") return metadata.geolocation?.longitude;
  return metadata[path];
};

const getOptions = (stores, path) =>
  [...new Set(stores.map((store) => getStoreValue(store, path)).filter(Boolean))]
    .map(String)
    .sort((left, right) => left.localeCompare(right))
    .map((value) => ({ id: value, displayText: value }));

const buildFilterConfig = (stores) => {
  const properties = Object.fromEntries(
    FILTERS.map(({ path, label }) => [
      path,
      { type: "array", items: { type: "string" }, title: label },
    ])
  );
  const elements = FILTERS.map(({ path, label }) => ({
    type: "Control",
    scope: "#/properties/" + path,
    component: "staticDropdownSelect",
    placeholder: label,
    options: { childList: getOptions(stores, path), selection: "multiple" },
  }));
  const jsonSchema = { type: "object", properties };
  const uiSchema = { type: "HorizontalLayout", elements };

  return {
    withoutDebounce: { jsonSchema, uiSchema },
    allFilters: { jsonSchema, uiSchema },
  };
};

const hasValue = (value) =>
  Array.isArray(value)
    ? value.length > 0
    : value !== null && value !== undefined && String(value).trim() !== "";

const matchesFilters = (store, filters) =>
  Object.entries(filters).every(([path, selectedValue]) => {
    if (!hasValue(selectedValue)) return true;
    const selectedValues = Array.isArray(selectedValue)
      ? selectedValue
      : [selectedValue];
    return selectedValues.map(String).includes(String(getStoreValue(store, path)));
  });

const toRow = (store) => ({
  id: store.id,
  name: getStoreValue(store, "name") || "-",
  address: getStoreValue(store, "address") || "-",
  state: getStoreValue(store, "state") || "-",
  city: getStoreValue(store, "city") || "-",
  postcode: getStoreValue(store, "postcode") || "-",
  latitude: getStoreValue(store, "latitude") ?? "-",
  longitude: getStoreValue(store, "longitude") ?? "-",
  category: getStoreValue(store, "category") || "-",
  country: getStoreValue(store, "country") || "-",
});

export default function StoreSelector({
  stores,
  initialSelection,
  onConfirm,
  onBack,
  onCancel,
}) {
  const { t } = useTranslation("StoreOutcome");
  const [selectedStoreIds, setSelectedStoreIds] = useState(
    initialSelection?.storeIds || []
  );
  const [filters, setFilters] = useState({});
  const [searchTerm, setSearchTerm] = useState("");

  const filterConfig = useMemo(() => buildFilterConfig(stores), [stores]);
  const displayedStores = useMemo(
    () => stores.filter((store) => matchesFilters(store, filters)),
    [filters, stores]
  );
  const rows = useMemo(
    () =>
      searchHandler(
        searchTerm,
        [
          "name",
          "address",
          "state",
          "city",
          "postcode",
          "retailer",
          "category",
          "country",
        ],
        displayedStores.map(toRow)
      ),
    [displayedStores, searchTerm]
  );
  const selectedStores = useMemo(
    () => stores.filter((store) => selectedStoreIds.includes(store.id)),
    [selectedStoreIds, stores]
  );
  const hasFilters = Object.values(filters).some(hasValue);

  const handleConfirm = () => {
    if (!selectedStores.length) return;
    const selectedCategories = [
      ...new Set(selectedStores.map((store) => store.category).filter(Boolean)),
    ];
    const category = selectedCategories.length === 1 ? selectedCategories[0] : null;
    const isEntireGroup =
      initialSelection?.selectionType === STORE_SELECTION_TYPES.STORE_GROUP &&
      category &&
      stores
        .filter((store) => store.category === category)
        .every((store) => selectedStoreIds.includes(store.id));
    onConfirm({
      selectionType: isEntireGroup
        ? STORE_SELECTION_TYPES.STORE_GROUP
        : STORE_SELECTION_TYPES.STORE,
      category,
      stores: selectedStores,
    });
  };

  return (
    <div className="store-selector" data-testid="store-selector">
      <div className="store-selector__header">
        <Typography variant="h6" component="h2">
          {t("AddStoresTitle")}
        </Typography>
        <IconButton aria-label="Close" onClick={onCancel} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>

      <ConditionFilters
        filterConfig={filterConfig}
        filterData={filters}
        onFilterChange={setFilters}
        onClear={() => setFilters({})}
        onSearch={setSearchTerm}
        clearButtonText={t("ClearFilters")}
      />

      <div className="store-selector__table">
        <SelectableTable
          rows={rows}
          headerData={HEADERS}
          selectedIds={selectedStoreIds}
          onSelectionChange={setSelectedStoreIds}
          className="campaign-store-listing"
          noDataLabel={hasFilters ? t("NoStoresForFilters") : t("NoStores")}
          rowsPerPage={10}
          showRowsPerPage
          highlightSelection
        />
      </div>

      <div className="store-selector__actions">
        <Button
          variant="tertiary"
          text={onBack ? t("Back") : t("Cancel")}
          size="medium"
          onClick={onBack || onCancel}
        />
        <Button
          variant="primary"
          text={t("AddSelectedStores", { count: selectedStores.length })}
          size="medium"
          onClick={handleConfirm}
          disabled={!selectedStores.length}
        />
      </div>
    </div>
  );
}

StoreSelector.propTypes = {
  stores: PropTypes.arrayOf(PropTypes.object).isRequired,
  initialSelection: PropTypes.shape({
    selectionType: PropTypes.string,
    storeIds: PropTypes.arrayOf(PropTypes.string),
  }),
  onConfirm: PropTypes.func.isRequired,
  onBack: PropTypes.func,
  onCancel: PropTypes.func.isRequired,
};

StoreSelector.defaultProps = {
  initialSelection: null,
  onBack: null,
};
